import * as THREE from 'three';
import { COMPONENT_KEYS } from './playmatComponentEditor';

// Renders the 12 playmat components as live 3D meshes on top of the
// playmat surface while the placement editor is open. Each mesh is
// a textured Plane sized to the SVG's natural pixel dimensions
// translated to world units. P2 components are rotated 180° around
// their local Y axis so they read upright from P2's seat. Same
// transformation the baked UI texture uses (ctx.rotate(Math.PI) in
// the generator). When the editor closes, all meshes are removed
// and the baked texture takes over again.
//
// Each mesh's userData carries `componentKey` so the editor can
// identify which component the cursor picked.

const TABLE_WIDTH = 200;
const TABLE_HEIGHT = Math.round(200 / (10300 / 7200));
const CANVAS_W = 10300;
const Y_PLACEMENT = 0.09; // above the UI overlay (0.06) so they pick first
const Y_HIGHLIGHT = 0.10;

// Slot illustrations render at a fixed world size matching their
// cards' on-board footprint × 15% oversize. Spellbook + cemetery
// are portrait (vertical cards). Atlas is landscape (horizontal
// cards on the board). Mirrors the constants in
// uiOverlayGenerator.js so what the dev sees in the editor matches
// what gets baked into the texture.
const CARD_WORLD_WIDTH = 6.35 * 1.75;
const CARD_WORLD_HEIGHT = 8.89 * 1.75;
const SLOT_OVERSIZE_FACTOR = 1.15;
// Mirrors ATLAS_SLOT_SCALE in uiOverlayGenerator.js. Atlas-only bump
// so the editor preview matches the baked texture.
const ATLAS_SLOT_SCALE = 1.2;
const PORTRAIT_SLOT_W = CARD_WORLD_WIDTH * SLOT_OVERSIZE_FACTOR;
const PORTRAIT_SLOT_H = CARD_WORLD_HEIGHT * SLOT_OVERSIZE_FACTOR;
const LANDSCAPE_SLOT_W = CARD_WORLD_HEIGHT * SLOT_OVERSIZE_FACTOR * ATLAS_SLOT_SCALE;
const LANDSCAPE_SLOT_H = CARD_WORLD_WIDTH * SLOT_OVERSIZE_FACTOR * ATLAS_SLOT_SCALE;
const PORTRAIT_SLOT_FILES = new Set(['spellbook.svg', 'cemetery.svg']);
const LANDSCAPE_SLOT_FILES = new Set(['atlas.svg']);

const COMPONENT_BASE_URL = '/game-assets/playmats/components';

const COLOR_HIGHLIGHT = 0xa2f0c4;

// Cache: image natural dimensions per SVG file (so re-opening the
// editor doesn't re-load every SVG).
const dimensionCache = new Map();
const textureCache = new Map();

function pixelToWorld(px) {
  return (px / CANVAS_W) * TABLE_WIDTH;
}

async function loadComponentTexture(file) {
  if (textureCache.has(file)) return textureCache.get(file);
  const loader = new THREE.TextureLoader();
  const url = `${COMPONENT_BASE_URL}/${file}`;
  const promise = new Promise((resolve, reject) => {
    loader.load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        if (tex.image?.naturalWidth) {
          dimensionCache.set(file, {
            w: tex.image.naturalWidth,
            h: tex.image.naturalHeight,
          });
        }
        resolve(tex);
      },
      undefined,
      (err) => reject(err),
    );
  });
  textureCache.set(file, promise);
  return promise;
}

export class PlaymatComponentRenderer {
  constructor(scene) {
    this.scene = scene;
    this.meshes = new Map(); // key -> mesh
    this.highlightMeshes = new Map(); // key -> outline rect mesh
  }

  async update(placements, activeKey) {
    if (!this.scene) return;

    // Add or update a mesh per component key.
    for (const def of COMPONENT_KEYS) {
      if (def.disabled) {
        this.removeMesh(def.key);
        continue;
      }
      const placement = placements[def.key];
      if (!placement) {
        this.removeMesh(def.key);
        continue;
      }
      let entry = this.meshes.get(def.key);
      if (!entry) {
        try {
          const tex = await loadComponentTexture(def.file);
          const dim = dimensionCache.get(def.file) || { w: 1000, h: 1000 };
          let worldW = pixelToWorld(dim.w);
          let worldH = pixelToWorld(dim.h);
          if (PORTRAIT_SLOT_FILES.has(def.file)) {
            worldW = PORTRAIT_SLOT_W;
            worldH = PORTRAIT_SLOT_H;
          } else if (LANDSCAPE_SLOT_FILES.has(def.file)) {
            worldW = LANDSCAPE_SLOT_W;
            worldH = LANDSCAPE_SLOT_H;
          }
          const geo = new THREE.PlaneGeometry(worldW, worldH);
          const mat = new THREE.MeshBasicMaterial({
            map: tex,
            transparent: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -4,
            polygonOffsetUnits: -4,
          });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.rotation.x = -Math.PI / 2;
          if (def.player === 2) mesh.rotation.z = Math.PI;
          mesh.userData = { type: 'playmatComponent', componentKey: def.key };
          this.scene.scene.add(mesh);
          entry = { mesh, def, worldW, worldH };
          this.meshes.set(def.key, entry);
        } catch (err) {
          console.warn(`[componentRenderer] ${def.file} failed:`, err.message);
          continue;
        }
      }
      entry.mesh.position.set(placement.x, Y_PLACEMENT, placement.z);
      entry.mesh.renderOrder = 10;
    }

    // Highlight the active (currently-dragging) component with a
    // green wireframe outline so the user can tell what they're
    // grabbing even when components overlap.
    this.updateHighlight(activeKey);
  }

  updateHighlight(activeKey) {
    // Remove all existing outlines.
    for (const [k, m] of this.highlightMeshes) {
      this.scene.scene.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    }
    this.highlightMeshes.clear();
    if (!activeKey) return;
    const entry = this.meshes.get(activeKey);
    if (!entry) return;
    const { mesh, worldW, worldH, def } = entry;
    const half = (w) => w / 2;
    const verts = [
      -half(worldW), 0, -half(worldH),  half(worldW), 0, -half(worldH),
       half(worldW), 0, -half(worldH),  half(worldW), 0,  half(worldH),
       half(worldW), 0,  half(worldH), -half(worldW), 0,  half(worldH),
      -half(worldW), 0,  half(worldH), -half(worldW), 0, -half(worldH),
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    const mat = new THREE.LineBasicMaterial({ color: COLOR_HIGHLIGHT, transparent: true, opacity: 0.9 });
    const outline = new THREE.LineSegments(geo, mat);
    outline.position.set(mesh.position.x, Y_HIGHLIGHT, mesh.position.z);
    outline.renderOrder = 50;
    this.scene.scene.add(outline);
    this.highlightMeshes.set(activeKey, outline);
  }

  removeMesh(key) {
    const entry = this.meshes.get(key);
    if (!entry) return;
    this.scene.scene.remove(entry.mesh);
    entry.mesh.geometry.dispose();
    entry.mesh.material.dispose();
    this.meshes.delete(key);
  }

  getPickableMeshes() {
    return Array.from(this.meshes.values()).map((e) => e.mesh);
  }

  clear() {
    for (const [k] of this.meshes) this.removeMesh(k);
    for (const [, m] of this.highlightMeshes) {
      this.scene?.scene.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    }
    this.highlightMeshes.clear();
  }

  dispose() { this.clear(); }
}
